/**
 * TaskCountResolver
 *
 * Extracts task counts from event descriptions.
 * Uses EventDescriptionSectionParser to parse checklist items
 * and returns structured count data for UI indicators.
 */

import { EventDescriptionSectionParser } from './EventDescriptionSectionParser';

export type TaskCountResult = {
  openCount: number;
  totalCount: number;
  allCompleted: boolean;
};

export class TaskCountResolver {
  /**
   * Resolve task counts from an event description string.
   * Returns null if no tasks exist (no indicator should be shown).
   */
  static resolve(description: string | null | undefined): TaskCountResult | null {
    if (!description) return null;

    const parsed = EventDescriptionSectionParser.parse(description);
    const tasks = parsed.tasks;

    if (tasks.length === 0) return null;

    const completedCount = tasks.filter(t => t.completed).length;
    const openCount = tasks.length - completedCount;

    return {
      openCount,
      totalCount: tasks.length,
      allCompleted: openCount === 0,
    };
  }

  /**
   * Build a TaskCountResult from pre-computed open/total counts.
   * Returns null if totalCount is 0 or undefined.
   */
  static fromCounts(
    openCount: number | undefined,
    totalCount: number | undefined,
  ): TaskCountResult | null {
    if (!totalCount || totalCount === 0) return null;

    return {
      openCount: openCount ?? 0,
      totalCount,
      allCompleted: (openCount ?? 0) === 0,
    };
  }
}
